import _t from 'i18n!nls/portfolio-projects';

export const CHARACTER_LIMIT = 500;
export const SMALL_CHARACTER_LIMIT = 200;

export const ONE_MONTH = 1000 * 60 * 60 * 24 * 30;
export const SIX_MONTHS = ONE_MONTH * 6;

export const getPortfolioProjectItemTranslations = () => ({
  PORTFOLIO_PROJECT_ITEM_TITLE: _t('Last Step: Showcase Your Project'),
  PORTFOLIO_PROJECT_ITEM_EFFORT: _t('30 min'),
  PORTFOLIO_PROJECT_ITEM_EFFORT_DESCRIPTION: _t('30 min estimated time to complete'),
  PORTFOLIO_PROJECT_ITEM_DESCRIPTION: _t(
    'Stand out by presenting your unique solution and approach to achieving the project objectives'
  ),
  PORTFOLIO_PROJECT_ITEM_LABEL: _t('Add this project to your profile'),
  PORTFOLIO_PROJECT_ITEM_LABEL_PUBLISHED: _t('Edit this project on your profile'),
});

export const getPortfolioBuilderTranslations = () => ({
  PORTFOLIO_BUILDER_TITLE: _t('Add your project to your profile'),
  PORTFOLIO_BUILDER_TITLE_PUBLISHED: _t('Edit project'),
});

export const getPortfolioBuilderGuideTranslations = () => ({
  PORTFOLIO_BUILDER_GUIDE_TITLE: _t('How to showcase your project'),
  PORTFOLIO_BUILDER_GUIDE_DESCRIPTION: _t(
    'Follow these tips to effectively demonstrate your problem-solving, critical thinking, and communication skills.'
  ),
  PORTFOLIO_BUILDER_GUIDE_STEP_1_TITLE: _t('Include relevant details'),
  PORTFOLIO_BUILDER_GUIDE_STEP_1_DESCRIPTION: _t(
    'Give a clear picture of your thought process by adding details about the decisions you made and the steps you took to complete the project. It’s important to show your work!'
  ),
  PORTFOLIO_BUILDER_GUIDE_STEP_2_TITLE: _t('Lead with your key findings'),
  PORTFOLIO_BUILDER_GUIDE_STEP_2_DESCRIPTION: _t(
    'When summarizing your project, focus on the results that show the most positive impact of your work. What benefits do your recommendations provide?'
  ),
  PORTFOLIO_BUILDER_GUIDE_STEP_3_TITLE: _t('Keep it succinct and professional'),
  PORTFOLIO_BUILDER_GUIDE_STEP_3_DESCRIPTION: _t(
    'Keep your responses concise and to the point. Avoid slang or jargon that could make it difficult for the employer to understand what your solution has to offer.'
  ),
  PORTFOLIO_BUILDER_GUIDE_LEARN_MORE_LABEL: _t('Learn more about writing effective case studies'),
});

export const getPortfolioBuilderFormTranslations = () => ({
  // Heading section
  PORTFOLIO_BUILDER_FORM_HEADING: _t('Project overview'),
  PORTFOLIO_BUILDER_FORM_HEADING_SUPPORT_TEXT: _t(
    'Describe your key findings, recommendations, and process for this project. This information will be visible to anyone who views this project from your profile.'
  ),
  // Select project section
  PORTFOLIO_BUILDER_FORM_SELECT_PROJECT_LABEL: _t('Select project'),
  PORTFOLIO_BUILDER_FORM_AUTO_POPULATED_PROJECT_LABEL: _t('Project title'),
  PORTFOLIO_BUILDER_FORM_SELECT_PROJECT_PLACEHOLDER: _t('Type to search'),
  // Project artifact(s) section
  PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_LABEL: _t('Project link(s)'),
  PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_SUPPORT_TEXT: _t('Provide link(s) to your completed project.'),
  PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_CONTEXTUAL_HELP: _t(
    'If you built your project on Coursera, go back to your Coursera project to generate a link and include it here. If you worked off-platform, make sure your project is hosted on a publicly-viewable URL (like Github or Google Drive).'
  ),
  PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_TITLE_LABEL: _t('Link title'),
  PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_TITLE_PLACEHOLDER: _t('E.g. GitHub Repo, Personal Website'),
  PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_URL_LABEL: _t('URL'),
  PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_URL_PLACEHOLDER: _t('Provide URL'),
  PORTFOLIO_BUILDER_FORM_PROJECT_ARTIFACT_ADD_LABEL: _t('Add link'),
  // Project link section
  PORTFOLIO_BUILDER_FORM_PROJECT_LINK_LABEL: _t('Project link'),
  PORTFOLIO_BUILDER_FORM_PROJECT_LINK_PLACEHOLDER: _t('Enter the URL to your completed project'),
  PORTFOLIO_BUILDER_FORM_PROJECT_LINK_SUPPORT_TEXT: _t('Provide a link to your completed project.'),
  // Summary section
  PORTFOLIO_BUILDER_FORM_SUMMARY_LABEL: _t('Summary'),
  PORTFOLIO_BUILDER_FORM_SUMMARY_PLACEHOLDER: _t(`Enter a brief summary of your project's highlights`),
  PORTFOLIO_BUILDER_FORM_SUMMARY_SUPPORT_TEXT: _t(
    'Summarize the techniques you used to complete your project and the highlights or business impact of your solution. '
  ),
  PORTFOLIO_BUILDER_FORM_SUMMARY_CONTEXTUAL_HELP: _t(
    'This section gives viewers a quick introduction to your project on your profile. To stand out, use this space to clearly and concisely describe the key techniques you used and the potential business impact of your work.'
  ),
  // Approach section
  PORTFOLIO_BUILDER_FORM_APPROACH_LABEL: _t('Approach'),
  PORTFOLIO_BUILDER_FORM_APPROACH_PLACEHOLDER: _t('Enter a detailed description of your approach'),
  PORTFOLIO_BUILDER_FORM_APPROACH_SUPPORT_TEXT: _t('Describe the steps you took to complete your project. '),
  PORTFOLIO_BUILDER_FORM_APPROACH_CONTEXTUAL_HELP: _t(
    'This is where viewers will learn about the process you used to complete the project, and see your problem-solving skills in action. Use this space to give more details about the tools and the techniques you used to achieve the project objectives, and the thought process behind the steps you took to get there.'
  ),
  // Solution section
  PORTFOLIO_BUILDER_FORM_SOLUTION_LABEL: _t('Solution'),
  PORTFOLIO_BUILDER_FORM_SOLUTION_PLACEHOLDER: _t('Enter a detailed explanation of your solution'),
  PORTFOLIO_BUILDER_FORM_SOLUTION_SUPPORT_TEXT: _t('Explain how your solution achieved the project objectives.'),
  PORTFOLIO_BUILDER_FORM_SOLUTION_CONTEXTUAL_HELP: _t(
    'This is where viewers will look for details about what made your project successful and assess your ability to describe how your work relates to business goals. Use this space to explain the benefits of your proposed solution so they’re clear to the viewers. Make sure to refer back to the objectives outlined in the project scenario.'
  ),
  // Adding a project
  PORTFOLIO_BUILDER_FORM_PUBLISH_LABEL: _t('Add project to profile'),
  PORTFOLIO_BUILDER_FORM_SAVE_DRAFT_LABEL: _t('Save draft'),
  // Editing a project
  PORTFOLIO_BUILDER_FORM_UNPUBLISH_LABEL: _t('Hide from profile'),
  PORTFOLIO_BUILDER_FORM_SAVE_CHANGES_LABEL: _t('Save changes'),
  // Last saved section
  PORTFOLIO_BUILDER_FORM_SAVING_LABEL: _t('Saving...'),
  PORTFOLIO_BUILDER_FORM_LAST_SAVED_LABEL: _t('Last saved: {date}'),
  // Error messages
  PORTFOLIO_BUILDER_FORM_ERROR_MESSAGE: _t('Something went wrong. Please try again.'),
});

export const getPortfolioProjectPublishDialogTranslations = () => ({
  PORTFOLIO_PROJECT_PUBLISH_DIALOG_TITLE: _t('Ready to add your project?'),
  PORTFOLIO_PROJECT_PUBLISH_DIALOG_DESCRIPTION: _t(
    'You can always view, edit, or hide your project directly from your profile after you add it.'
  ),
  PORTFOLIO_PROJECT_PUBLISH_DIALOG_CANCEL_LABEL: _t('Cancel'),
  PORTFOLIO_PROJECT_PUBLISH_DIALOG_PUBLISH_LABEL: _t('Add project to profile'),
});

export const getPortfolioProjectPublishSuccessDialogTranslations = () => ({
  PORTFOLIO_PROJECT_PUBLISH_SUCCESS_DIALOG_TITLE: _t('Your project has been added to your profile!'),
  PORTFOLIO_PROJECT_PUBLISH_SUCCESS_DIALOG_DESCRIPTION: _t(
    'Great work! You’re one step closer to finding your dream job.'
  ),
  PORTFOLIO_PROJECT_PUBLISH_SUCCESS_DIALOG_CONFIRM_LABEL: _t('Got it'),
  PORTFOLIO_PROJECT_PUBLISH_SUCCESS_DIALOG_VIEW_PROJECT_LABEL: _t('View project'),
});

export const getPortfolioProjectHideDialogTranslations = () => ({
  PORTFOLIO_PROJECT_HIDE_DIALOG_TITLE: _t('Are you sure you want to hide this project?'),
  PORTFOLIO_PROJECT_HIDE_DIALOG_DESCRIPTION: _t(
    'This means that recruiters won’t be able to see your work. Are you sure?'
  ),
  PORTFOLIO_PROJECT_HIDE_DIALOG_HIDE_LABEL: _t('Hide project'),
  PORTFOLIO_PROJECT_HIDE_DIALOG_CANCEL_LABEL: _t('Cancel'),
});
